import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { payoutService, groupService } from "@/services";
import type { KametiGroup, PayoutStatus, PayoutView } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canApprovePayouts, canReversePayouts } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";
import { paymentModeLabel } from "@/pages/collection/components/labels";
import { payoutStatusMeta } from "../components/labels";
import { ReversalModal } from "../process/components/ReversalModal";

const STATUS_OPTIONS = [
  { value: "pending_approval", label: "Awaiting approval" },
  { value: "approved", label: "Approved" },
  { value: "partially_disbursed", label: "Partially paid" },
  { value: "disbursed", label: "Disbursed" },
  { value: "reversed", label: "Reversed" },
];

const MODE_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cheque", label: "Cheque" },
];

export default function PayoutHistoryPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const toast = useToast();
  const canReverse = canReversePayouts(role);
  const canApprove = canApprovePayouts(role);

  const [payouts, setPayouts] = useState<PayoutView[] | null>(null);
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [groupId, setGroupId] = useState("");
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState("");
  const [range, setRange] = useState<DateRange>({ start: null, end: null });
  const [reverseTarget, setReverseTarget] = useState<PayoutView | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, g] = await Promise.all([payoutService.listPayouts(), groupService.listGroups()]);
      setPayouts(p);
      setGroups(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load payout history.");
      setPayouts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let rows = payouts ?? [];
    if (groupId) rows = rows.filter((p) => p.groupId === groupId);
    if (status) rows = rows.filter((p) => p.status === status);
    if (mode) rows = rows.filter((p) => p.disbursements.some((d) => d.mode === mode));
    if (range.start) rows = rows.filter((p) => new Date(p.createdAt) >= new Date(range.start as string));
    if (range.end) {
      const end = new Date(range.end as string);
      end.setHours(23, 59, 59, 999);
      rows = rows.filter((p) => new Date(p.createdAt) <= end);
    }
    return rows;
  }, [payouts, groupId, status, mode, range]);

  const activeFilters = (groupId ? 1 : 0) + (status ? 1 : 0) + (mode ? 1 : 0) + (range.start ? 1 : 0);

  const clearFilters = () => {
    setGroupId("");
    setStatus("");
    setMode("");
    setRange({ start: null, end: null });
  };

  const doReverse = async (reason: string) => {
    if (!reverseTarget) return false;
    try {
      await payoutService.reversePayout(reverseTarget.id, reason, "Staff User");
      toast.warning("Payout reversed", `${reverseTarget.voucherNumber} disbursement cancelled and marked as reversed.`);
      await load();
      return true;
    } catch (err) {
      toast.error("Could not reverse", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  const doReopen = async (p: PayoutView) => {
    try {
      await payoutService.reopenPayout(p.id);
      toast.success("Payout re-opened", `${p.voucherNumber} is now awaiting review & approval.`);
      await load();
    } catch (err) {
      toast.error("Could not re-open", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const columns: DataTableColumn<PayoutView>[] = [
    {
      id: "voucher",
      header: "Voucher",
      accessor: (p) => <span className="tnum text-xs font-semibold text-primary-700">{p.voucherNumber}</span>,
      sortValue: (p) => p.voucherNumber,
      sortable: true,
      width: "120px",
    },
    {
      id: "group",
      header: "Group / Cycle",
      accessor: (p) => (
        <div>
          <span className="font-medium text-foreground-900">{p.groupName}</span>
          <span className="tnum ml-1 text-xs text-foreground-400">{p.groupCode}</span>
          <span className="tnum ml-2 text-xs text-foreground-500">Cycle {p.cycle}</span>
        </div>
      ),
      sortValue: (p) => `${p.groupName} ${p.cycle}`,
      sortable: true,
    },
    {
      id: "period",
      header: "Period",
      accessor: (p) => <span className="tnum text-xs text-foreground-600">{p.periodLabel}</span>,
      width: "110px",
    },
    {
      id: "winner",
      header: "Winner",
      accessor: (p) => (
        <div>
          <span className="font-medium text-foreground-900">{p.winnerName}</span>
          <span className="tnum ml-1 text-xs text-foreground-400">· {p.winnerTicketNumber}</span>
        </div>
      ),
      sortValue: (p) => p.winnerName,
      sortable: true,
    },
    {
      id: "prize",
      header: "Prize",
      align: "right",
      accessor: (p) => <CurrencyDisplay amount={p.prize} hideSymbol />,
      sortValue: (p) => p.prize,
      sortable: true,
      width: "110px",
    },
    {
      id: "netPayable",
      header: "Net payable",
      align: "right",
      accessor: (p) => <span className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={p.netPayable} hideSymbol /></span>,
      sortValue: (p) => p.netPayable,
      sortable: true,
      width: "120px",
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, r) => s + r.netPayable, 0)} hideSymbol />,
    },
    {
      id: "amountPaid",
      header: "Paid",
      align: "right",
      accessor: (p) => <CurrencyDisplay amount={p.amountPaid} hideSymbol tone={p.status === "reversed" ? "danger" : "default"} />,
      sortValue: (p) => p.amountPaid,
      sortable: true,
      width: "110px",
      footerValue: (rows) => <CurrencyDisplay amount={rows.filter((r) => r.status !== "reversed").reduce((s, r) => s + r.amountPaid, 0)} hideSymbol />,
    },
    {
      id: "status",
      header: "Status",
      accessor: (p) => {
        const meta = payoutStatusMeta(p.status);
        return (
          <div className="flex flex-col gap-0.5">
            <Badge color={meta.color} dot>{meta.label}</Badge>
            {p.status === "reversed" && p.reversalReason ? (
              <span className="text-[10px] text-danger-600 truncate max-w-[150px]" title={p.reversalReason}>
                {p.reversalReason}
              </span>
            ) : null}
          </div>
        );
      },
      sortValue: (p) => p.status,
      sortable: true,
      width: "140px",
    },
    {
      id: "actions",
      header: "Action",
      align: "right",
      width: "170px",
      hideable: false,
      accessor: (p) => (
        <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" icon={<i className="ri-eye-line" />} onClick={() => navigate(`/payout/${p.id}`)}>
            View
          </Button>
          {p.status === "reversed" && canApprove ? (
            <Button size="sm" variant="outline" icon={<i className="ri-restart-line" />} onClick={() => doReopen(p)}>
              Re-open
            </Button>
          ) : null}
          {p.status === "disbursed" && canReverse ? (
            <Button size="sm" variant="ghost" icon={<i className="ri-close-circle-line" />} className="text-danger-600 hover:bg-danger-50" onClick={() => setReverseTarget(p)}>
              Reverse
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/payout")}>
      Back
    </Button>
  );

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Payout" to="/payout" />
            <BreadcrumbItem label="History" isLast />
          </>
        }
        title="Payout history"
        subtitle="All payouts across all groups"
        actions={back}
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!payouts ? (
        <div className="h-72 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <Card>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
            >
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as PayoutStatus | "")}
              className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <DateRangePicker value={range} onChange={setRange} />
            {activeFilters > 0 ? (
              <button type="button" onClick={clearFilters} className="cursor-pointer text-xs font-medium text-primary-600 hover:underline">
                Clear filters ({activeFilters})
              </button>
            ) : null}
          </div>

          <DataTable<PayoutView>
            columns={columns}
            rows={filtered}
            rowKey={(p) => p.id}
            emptyMessage="No payouts match these filters"
            emptyDescription="Try a wider date range, or check the payout dashboard."
            onRowClick={(p) => navigate(`/payout/${p.id}`)}
            showTotals
            totalLabel="Total"
            exportable
            exportFilename="payout-history"
            defaultPageSize={12}
            initialSort={{ id: "date", dir: "desc" }}
          />
        </Card>
      )}

      {reverseTarget ? (
        <ReversalModal payout={reverseTarget} onClose={() => setReverseTarget(null)} onConfirm={doReverse} />
      ) : null}
    </div>
  );
}